import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../cn";

// Flat by default (design brief §2.5): 1px hairline, 12px radius, no resting shadow.
// Elevation is reserved for overlays (drawers, modals, popovers, toasts).
const card = cva("bg-card transition-colors duration-200", {
  variants: {
    variant: {
      default: "rounded-card border border-line",
      // Clickable surfaces firm their border on hover — the calm signal of interactivity.
      raised: "rounded-card border border-line cursor-pointer hover:border-strong",
      // Recessed grouping, no elevation.
      inset: "rounded-card border border-hairline bg-subtle",
      // The primary surface reads crafted: larger padding.
      hero: "rounded-card border border-line p-8",
    },
  },
  defaultVariants: { variant: "default" },
});

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof card> {}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div ref={ref} className={cn(card({ variant }), className)} {...props} />
  ),
);
Card.displayName = "Card";

export function CardHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-start justify-between gap-4 px-6 pt-6", className)} {...props} />;
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  return <h2 className={cn("text-base font-semibold tracking-tight text-ink", className)} {...props} />;
}

export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("text-sm leading-relaxed text-mute", className)} {...props} />;
}

export function CardBody({ className, compact, ...props }: React.HTMLAttributes<HTMLDivElement> & { compact?: boolean }) {
  return <div className={cn(compact ? "p-4" : "p-6", className)} {...props} />;
}

/** Right-aligned header actions slot for progressive disclosure (view all / filters). */
export function CardToolbar({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex items-center gap-1.5", className)} {...props} />;
}

/** Quiet footer for 'last updated / view all' meta — turns a dead-end into a next step. */
export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 border-t border-hairline bg-subtle/40 px-6 py-2.5 text-xs text-mute",
        className,
      )}
      {...props}
    />
  );
}
